/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { forwardRef } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { jsx } from '@emotion/react';
import { useIntl } from 'react-intl-next';

import Button from '@atlaskit/button/new';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import { Text } from '@atlaskit/primitives';

import { AI_MODAL_SCREEN_CLASS } from '../../../utils/constants';
import { FloatingContainer } from '../../components/FloatingContainer/FloatingContainer';
import type { Breakpoint } from '../../hooks/useElementBreakpoints';

import messages from './messages';
import {
	buttonContainerStyles,
	getContainerStyles,
	getIconStyles,
	getTextContainerStyles,
	iconColor,
} from './styles';

export function ErrorMessage({
	children,
	breakpoint,
}: {
	children: React.ReactNode;
	breakpoint: Breakpoint;
}) {
	const { formatMessage } = useIntl();
	return (
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
		<div css={getTextContainerStyles(breakpoint)}>
			{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766 */}
			<span css={getIconStyles(breakpoint)}>
				<WarningIcon label={formatMessage(messages.warningIconLabel)} primaryColor={iconColor} />
			</span>

			<Text as="p">{children}</Text>
		</div>
	);
}

export function ErrorButton({
	children,
	onClick,
	appearance,
	testId,
	autoFocus,
	disabled = false,
}: {
	testId?: string;
	children: React.ReactNode;
	onClick: () => void;
	appearance: 'subtle' | 'primary';
	disabled?: boolean;
	autoFocus?: boolean;
}) {
	return (
		<Button
			autoFocus={autoFocus}
			testId={testId}
			spacing="compact"
			onClick={onClick}
			type="button"
			appearance={appearance}
			isDisabled={disabled}
		>
			{children}
		</Button>
	);
}

export function ErrorButtonContainer({ children }: { children: React.ReactNode }) {
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
	return <div css={buttonContainerStyles}>{children}</div>;
}

// Need to use forwardRef because functional component cannot be passed a ref
// as a prop.
export const ErrorFloatingContainer = forwardRef(
	(
		{
			children,
			breakpoint,
		}: {
			breakpoint: Breakpoint;
			children: React.ReactNode;
		},
		ref: React.Ref<HTMLDivElement>,
	) => {
		return (
			<FloatingContainer
				ref={ref}
				data-testid="error-screen"
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
				className={`error-screen ${AI_MODAL_SCREEN_CLASS}`}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				css={getContainerStyles(breakpoint)}
				paddingAdjustments={{ top: 1, bottom: 1 }}
				rainbowBorder={false}
			>
				{children}
			</FloatingContainer>
		);
	},
);
